// Live plan read model -> privacy-minimised print source.
//
// This adapter formats dates and composes already-derived display facts. It does
// not fetch, re-count coverage or infer export permission. The server owns those
// decisions; @herefor/print owns the safe default projection.

import { instant, localDateOf, timeZone, type TimeZoneId } from "@herefor/domain-kernel";
import type { PrintNeedSource, PrintScheduleSource } from "@herefor/print";
import {
  CIRCLE_SETTINGS_MESSAGES,
  PRINT_SCHEDULE_MESSAGES as COPY,
  PRACTICAL_UPDATE_MESSAGES,
  PROXY_COMMITMENT_MESSAGES,
  STATUS_CONTEXT,
} from "@herefor/product-language";

import type { LivePlanResult } from "../../convex/plan";
import type { PracticalUpdatesForPrint } from "../../convex/practicalUpdates";
import { toLivePlanViews } from "../plan";

/**
 * Print needs the event's independently authored safe title as well as its private
 * title. This intersection keeps the privacy dependency explicit while the
 * generated Convex return remains the source for every other field.
 */
export type PrintableLivePlanResult = Omit<LivePlanResult, "events"> & {
  readonly events: readonly (LivePlanResult["events"][number] & {
    readonly shareTitle: string;
  })[];
};

/**
 * THE SUPPORTED PERSON COMES FROM THE PLAN READ (bead hf-86o).
 *
 * This adapter used to take a second argument — the circle-settings read — for one
 * field, `printContactText`, reaching the same record by a different route. The plan
 * read now carries the whole record, so the settings read is not print's business:
 * the print route still needs it to know whether this member may export at all, and
 * nothing on the page comes from it any more.
 */
export function toPrintScheduleSource(
  result: PrintableLivePlanResult,
  generatedAt = Date.now(),
  practicalUpdates: PracticalUpdatesForPrint = [],
): PrintScheduleSource {
  const zone = timeZone(result.timeZone);
  const views = toLivePlanViews(result);
  const eventViews = new Map(views.events.map((event) => [event.id, event]));
  const needViews = new Map(views.needs.map((need) => [need.id, need]));
  const printContactText = result.supportedPerson?.printContactText?.trim();
  const printContactPersonName = result.supportedPerson?.printContactPersonName?.trim();
  const updatesByNeed = groupUpdates(practicalUpdates, result.timeZone);

  return {
    planName: result.displayName,
    // Her own name, for her own sheet (PRT-002, §25.12). Null when the circle has
    // recorded none, which heads that page neutrally instead of guessing.
    supportedPersonName: result.supportedPerson?.chosenName ?? null,
    scheduleStartLocal: localDateOf(instant(generatedAt), zone),
    generatedAtLabel: COPY.printedAt(formatPrintInstant(generatedAt, zone)),
    freshnessWarning: COPY.freshness(result.displayName),
    // The plan title is seeded from her name (plan §28.4), so an unconfirmed spelling
    // reaches paper through the heading whether or not the page names her elsewhere.
    supportedPersonNotice:
      result.supportedPerson?.isPlaceholder === true
        ? CIRCLE_SETTINGS_MESSAGES.supportedPerson.placeholderNotice
        : null,
    contactPersonName:
      printContactPersonName === undefined || printContactPersonName === ""
        ? null
        : printContactPersonName,
    contactText:
      printContactText === undefined || printContactText === "" ? null : printContactText,
    emptyDayLabel: COPY.emptyDay,
    events: result.events.map((event) => {
      const view = eventViews.get(event.eventId);
      if (view === undefined) throw new Error("Printable event is missing its live-plan view.");

      return {
        id: view.id,
        privateTitle: view.title,
        discreetTitle: event.shareTitle.trim() || COPY.eventFallbackTitle,
        localDate: view.dateLabel ?? "",
        windowLabel: view.windowLabel,
        locationLabel: view.locationLabel,
        state: view.stateKey,
        stateLabel: view.stateLabel,
        needs: view.needs.map((need) => toPrintNeed(need, updatesByNeed)),
      };
    }),
    standaloneNeeds: result.needs
      .filter((need) => need.eventId === undefined)
      .map((need) => {
        const view = needViews.get(need.supportNeedId);
        if (view === undefined) throw new Error("Printable need is missing its live-plan view.");
        return toPrintNeed(view, updatesByNeed);
      }),
  };
}

function toPrintNeed(
  need: ReturnType<typeof toLivePlanViews>["needs"][number],
  updatesByNeed: ReadonlyMap<string, PrintNeedSource["practicalUpdates"]>,
): PrintNeedSource {
  return {
    id: need.id,
    fullLabel: need.label,
    discreetLabel: COPY.discreetNeedLabel,
    windowLabel: need.windowLabel,
    localDate: need.dateLabel,
    state: need.stateKey,
    stateLabel: need.stateLabel,
    coverageCountLabel:
      need.counts === null
        ? null
        : STATUS_CONTEXT.coverageCount(need.counts.confirmed, need.counts.required),
    responsible: need.responsible.map((person) => {
      const discreetName = firstName(person.name);
      const discreetRecorder =
        person.recordedByName === null ? null : firstName(person.recordedByName);

      return {
        fullName: person.name,
        discreetName,
        fullProvenance:
          person.recordedByName === null
            ? null
            : PROXY_COMMITMENT_MESSAGES.provenance(
                person.name,
                person.recordedByName,
                person.agreementSource ?? "",
              ),
        discreetProvenance:
          discreetRecorder === null
            ? null
            : PROXY_COMMITMENT_MESSAGES.provenance(
                discreetName,
                discreetRecorder,
                person.agreementSource ?? "",
              ),
      };
    }),
    practicalUpdates: updatesByNeed.get(need.id) ?? [],
  };
}

function groupUpdates(
  updates: PracticalUpdatesForPrint,
  zone: string,
): ReadonlyMap<string, PrintNeedSource["practicalUpdates"]> {
  const grouped = new Map<string, PrintNeedSource["practicalUpdates"]>();
  for (const update of updates) {
    if (update.text === null) continue;
    const person = update.responsibleName ?? PRACTICAL_UPDATE_MESSAGES.formerMember;
    const recorder = update.recordedByName ?? PRACTICAL_UPDATE_MESSAGES.formerMember;
    const provenance =
      update.source === "proxy"
        ? PRACTICAL_UPDATE_MESSAGES.proxyProvenance(
            person,
            recorder,
            update.sourceChannel ?? PRACTICAL_UPDATE_MESSAGES.formerMember,
          )
        : PRACTICAL_UPDATE_MESSAGES.recorded(
            recorder,
            new Intl.DateTimeFormat("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
              timeZone: zone,
            }).format(new Date(update.createdAt)),
          );
    grouped.set(update.supportNeedId, [
      ...(grouped.get(update.supportNeedId) ?? []),
      { text: update.text, provenance },
    ]);
  }
  return grouped;
}

function firstName(name: string): string {
  const first = name.trim().split(/\s+/u)[0];
  return first === undefined || first === "" ? name : first;
}

function formatPrintInstant(at: number, zone: TimeZoneId): string {
  const date = new Date(at);
  const dateLabel = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: String(zone),
  }).format(date);
  const timeLabel = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: String(zone),
  }).format(date);
  return `${dateLabel} at ${timeLabel}`;
}
